import { motion } from "framer-motion";
import { DollarSign, TrendingUp, Users, Download, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, UserPlus, Gift, Clock, CheckCircle, ExternalLink, CreditCard, BarChart3, RefreshCw } from "lucide-react";
import { useCallback, useMemo } from "react";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";

// Apple icon SVG component
const AppleIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 384 512" fill="currentColor" className={className}>
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);

// Google Play icon SVG component
const PlayStoreIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 512 512" fill="currentColor" className={className}>
    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
  </svg>
);
import { useState } from "react";

const SOURCE_COLORS: Record<string, string> = {
  STRIPE: "#635BFF",       // Stripe brand indigo
  APP_STORE: "#FF9500",    // iOS system orange (UIColor.systemOrange)
  PLAY_STORE: "#34A853",   // Google green
  STRIPE_NEW: "#635BFF",
  APP_STORE_NEW: "#FF9500",
  PLAY_STORE_NEW: "#34A853",
  STRIPE_RENEWAL: "#635BFF",
  APP_STORE_RENEWAL: "#FF9500",
  PLAY_STORE_RENEWAL: "#34A853",
  Total: "#FFFFFF",        // white
};

const SOURCE_LABELS: Record<string, string> = {
  STRIPE: "Stripe",
  APP_STORE: "App Store",
  PLAY_STORE: "Play Store",
  STRIPE_NEW: "Stripe (new)",
  APP_STORE_NEW: "App Store (new)",
  PLAY_STORE_NEW: "Play Store (new)",
  STRIPE_RENEWAL: "Stripe (renewal)",
  APP_STORE_RENEWAL: "App Store (renewal)",
  PLAY_STORE_RENEWAL: "Play Store (renewal)",
};

const RENEWAL_SERIES_FOR_SOURCE: Record<string, string> = {
  STRIPE: "STRIPE_RENEWAL",
  APP_STORE: "APP_STORE_RENEWAL",
  PLAY_STORE: "PLAY_STORE_RENEWAL",
};

const NEW_SERIES_FOR_SOURCE: Record<string, string> = {
  STRIPE: "STRIPE_NEW",
  APP_STORE: "APP_STORE_NEW",
  PLAY_STORE: "PLAY_STORE_NEW",
};

const TIME_OF_DAY_COLORS = {
  today: "#8B5CF6",
  yesterday: "#06B6D4",
  sevenDaysAgo: "#F59E0B",
};
import { Link } from "wouter";
import { useAdminPayments, type PaymentOriginFilter, type PaymentTimeframeOption } from "../api/useAdminPayments";
import { stripeCustomerUrl, stripeCustomerBalanceUrl } from '../utils/stripeLinks';
import { useAdminPaymentCharts, type PaymentChartGranularity } from "../api/useAdminPaymentCharts";
import { useAdminReferrals } from "../api/useAdminReferrals";
import { Loader } from '@/components/Loader';


type StatusFilter = 'all' | 'succeeded' | 'pending' | 'failed' | 'refunded';
type ReferralStatusFilter = 'all' | 'pending' | 'used' | 'rewarded';

// Fee factor per processor for net revenue.
// Stripe: 2.9% + $0.30 per transaction (per-transaction fee applied in table only; charts use pct approx).
// App Store / Play Store: 15% (standard rate for most subscriptions after year 1).
const NET_PCT_FACTOR: Record<string, number> = {
  STRIPE: 1 - 0.029,
  APP_STORE: 0.85,
  PLAY_STORE: 0.85,
};

const applyNetToChartKey = (key: string, value: number): number => {
  for (const prefix of ["STRIPE", "APP_STORE", "PLAY_STORE"]) {
    if (key === prefix || key.startsWith(prefix + "_")) {
      return Math.max(0, value * (NET_PCT_FACTOR[prefix] ?? 1));
    }
  }
  return value;
};

const getNetAmountUsd = (amountUsd: number, processor: string): number => {
  if (processor === "STRIPE") return Math.max(0, amountUsd * (1 - 0.029) - 0.30);
  const factor = NET_PCT_FACTOR[processor];
  return factor != null ? Math.max(0, amountUsd * factor) : amountUsd;
};

// --- Payment Charts sub-component ---
function PaymentCharts({
  chartsResponse,
  isLoadingCharts,
  granularity,
  showNet,
}: {
  chartsResponse: ReturnType<typeof useAdminPaymentCharts>["data"];
  isLoadingCharts: boolean;
  granularity: PaymentChartGranularity;
  showNet: boolean;
}) {
  const chartData = chartsResponse?.data;

  const [enabledSources, setEnabledSources] = useState<Record<string, boolean>>({
    STRIPE: true,
    APP_STORE: true,
    PLAY_STORE: true,
    STRIPE_NEW: true,
    APP_STORE_NEW: true,
    PLAY_STORE_NEW: true,
    STRIPE_RENEWAL: true,
    APP_STORE_RENEWAL: true,
    PLAY_STORE_RENEWAL: true,
    Total: true,
  });

  const toggleSource = useCallback((source: string) => {
    setEnabledSources((prev) => ({ ...prev, [source]: !prev[source] }));
  }, []);

  type BreakdownPreset = "total" | "new" | "renewal";
  const applyBreakdownPreset = useCallback((preset: BreakdownPreset) => {
    setEnabledSources((prev) => ({
      ...prev,
      STRIPE_NEW: preset !== "renewal",
      APP_STORE_NEW: preset !== "renewal",
      PLAY_STORE_NEW: preset !== "renewal",
      STRIPE_RENEWAL: preset !== "new",
      APP_STORE_RENEWAL: preset !== "new",
      PLAY_STORE_RENEWAL: preset !== "new",
    }));
  }, []);

  const effectiveGranularity = chartData?.granularity ?? granularity;
  const formatBucketLabel = (dateStr: string) => {
    const d = new Date(dateStr + "T12:00:00");
    if (effectiveGranularity === "month") {
      return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
    }
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  const dailyData = (chartData?.dailyRevenue || []).map((d) => {
    const NET_KEYS = ["STRIPE", "APP_STORE", "PLAY_STORE", "STRIPE_RENEWAL", "APP_STORE_RENEWAL", "PLAY_STORE_RENEWAL"] as const;
    const netD = showNet
      ? NET_KEYS.reduce((acc, k) => ({ ...acc, [k]: applyNetToChartKey(k, d[k] ?? 0) }), { ...d })
      : d;
    return {
      ...netD,
      label: formatBucketLabel(d.date),
      STRIPE_NEW: Math.max(0, Math.round(((netD.STRIPE ?? 0) - (netD.STRIPE_RENEWAL ?? 0)) * 100) / 100),
      APP_STORE_NEW: Math.max(0, Math.round(((netD.APP_STORE ?? 0) - (netD.APP_STORE_RENEWAL ?? 0)) * 100) / 100),
      PLAY_STORE_NEW: Math.max(0, Math.round(((netD.PLAY_STORE ?? 0) - (netD.PLAY_STORE_RENEWAL ?? 0)) * 100) / 100),
      Total: showNet
        ? Math.round(((netD.STRIPE ?? 0) + (netD.APP_STORE ?? 0) + (netD.PLAY_STORE ?? 0)) * 100) / 100
        : d.Total,
    };
  });

  const timeOfDay = chartData?.timeOfDay || { data: [], todayStr: "", yesterdayStr: "", sevenDaysAgoStr: "" };

  // Current LA time bucketed to the nearest 15 minutes, used to draw a "now" line.
  // Recomputed whenever chartsResponse refreshes so a manual refresh advances the marker.
  const nowLabel = useMemo(() => {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Los_Angeles",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    }).formatToParts(new Date());
    const h = parseInt(parts.find((p) => p.type === "hour")?.value ?? "0", 10);
    const m = parseInt(parts.find((p) => p.type === "minute")?.value ?? "0", 10);
    const bucketM = Math.floor(m / 15) * 15;
    return `${String(h).padStart(2, "0")}:${String(bucketM).padStart(2, "0")}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartsResponse]);
  const hasNowLabel = timeOfDay.data.some((d: { label: string }) => d.label === nowLabel);

  // Labels in dailyData that fall on 7-day intervals counting back from today (LA time).
  // Only meaningful for daily granularity since week/month buckets don't align with arbitrary 7-day steps.
  const weeklyMarkerLabels = useMemo(() => {
    if (effectiveGranularity !== "day" || dailyData.length === 0) return [] as string[];
    const todayStr = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
    const todayMs = new Date(todayStr + "T12:00:00").getTime();
    const labels: string[] = [];
    for (const d of dailyData) {
      const ms = new Date(d.date + "T12:00:00").getTime();
      const diffDays = Math.round((todayMs - ms) / 86400000);
      if (diffDays >= 0 && diffDays % 7 === 0) labels.push(d.label);
    }
    return labels;
  }, [dailyData, effectiveGranularity]);

  const sources = chartData?.sources || [];
  const allSeries = [...sources, "Total"];
  // Series for the stacked breakdown chart, in stack order:
  // per source, the "new" slice on the bottom and "renewal" on top.
  const breakdownSeries = sources.flatMap((s) => {
    const newKey = NEW_SERIES_FOR_SOURCE[s];
    const renewalKey = RENEWAL_SERIES_FOR_SOURCE[s];
    return [newKey, renewalKey].filter((k): k is string => Boolean(k));
  });

  // Custom tooltip for daily chart
  const DailyTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const entries = payload.filter((e: any) => e.dataKey !== "Total");
    const total = entries.reduce((sum: number, e: any) => sum + (Number(e.value) || 0), 0);
    const totalNew = entries
      .filter((e: any) => typeof e.dataKey === "string" && e.dataKey.endsWith("_NEW"))
      .reduce((sum: number, e: any) => sum + (Number(e.value) || 0), 0);
    const totalRenewal = entries
      .filter((e: any) => typeof e.dataKey === "string" && e.dataKey.endsWith("_RENEWAL"))
      .reduce((sum: number, e: any) => sum + (Number(e.value) || 0), 0);
    const hasSplit = entries.some(
      (e: any) =>
        typeof e.dataKey === "string" &&
        (e.dataKey.endsWith("_NEW") || e.dataKey.endsWith("_RENEWAL"))
    );
    return (
      <div className="bg-neutral-900 border border-dark-border rounded-lg px-4 py-3 shadow-lg">
        <p className="text-sm font-medium mb-2">{label}</p>
        {entries.map((entry: any) => {
          const dotColor = SOURCE_COLORS[entry.dataKey] || entry.color;
          const isRenewal = typeof entry.dataKey === "string" && entry.dataKey.endsWith("_RENEWAL");
          return (
            <div key={entry.dataKey} className="flex items-center justify-between gap-6 text-sm">
              <span className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-sm"
                  style={{
                    background: isRenewal
                      ? `repeating-linear-gradient(45deg, ${dotColor} 0 1.5px, transparent 1.5px 3.5px)`
                      : dotColor,
                    border: isRenewal ? `1px solid ${dotColor}` : undefined,
                  }}
                />
                {SOURCE_LABELS[entry.dataKey] || entry.dataKey}
              </span>
              <span className="font-semibold">${entry.value?.toFixed(2)}</span>
            </div>
          );
        })}
        <div className="mt-2 pt-2 border-t border-dark-border space-y-1">
          {hasSplit && (
            <>
              <div className="flex items-center justify-between gap-6 text-sm">
                <span className="text-dark-muted">Total new</span>
                <span className="font-semibold">${totalNew.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between gap-6 text-sm">
                <span className="text-dark-muted">Total renewal</span>
                <span className="font-semibold">${totalRenewal.toFixed(2)}</span>
              </div>
            </>
          )}
          <div className="flex items-center justify-between gap-6 text-sm">
            <span className="flex items-center gap-2 font-medium">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: SOURCE_COLORS.Total }} />
              Total
            </span>
            <span className="font-bold">${total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Custom tooltip for time-of-day chart
  const TimeTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-neutral-900 border border-dark-border rounded-lg px-4 py-3 shadow-lg">
        <p className="text-sm font-medium mb-2">{label}</p>
        {payload.map((entry: any) => {
          const labels: Record<string, string> = {
            today: `Today (${timeOfDay.todayStr})`,
            yesterday: `Yesterday (${timeOfDay.yesterdayStr})`,
            sevenDaysAgo: `7 days ago (${timeOfDay.sevenDaysAgoStr})`,
          };
          return (
            <div key={entry.dataKey} className="flex items-center justify-between gap-6 text-sm">
              <span className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: entry.color }} />
                {labels[entry.dataKey] || entry.dataKey}
              </span>
              <span className="font-semibold">${entry.value?.toFixed(2)}</span>
            </div>
          );
        })}
      </div>
    );
  };

  if (isLoadingCharts) {
    return (
      <div className="card mb-8 flex items-center justify-center py-16">
        <Loader />
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      {/* Daily Revenue by Source */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold">
              {effectiveGranularity === "month" ? "Monthly" : effectiveGranularity === "week" ? "Weekly" : "Daily"} Revenue by Source
            </h2>
            <p className="text-dark-muted text-xs">Successful payments only</p>
          </div>
        </div>

        {/* Source toggles */}
        <div className="flex flex-wrap gap-2 mb-4">
          {allSeries.map((source) => {
            const active = enabledSources[source] !== false;
            return (
              <button
                key={source}
                onClick={() => toggleSource(source)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                  active
                    ? "border-transparent"
                    : "border-dark-border opacity-40"
                }`}
                style={{
                  background: active ? `${SOURCE_COLORS[source]}22` : undefined,
                  color: active ? SOURCE_COLORS[source] : undefined,
                }}
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: SOURCE_COLORS[source] }}
                />
                {SOURCE_LABELS[source] || source}
              </button>
            );
          })}
        </div>

        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dailyData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <defs>
                {allSeries.map((source) => (
                  <linearGradient key={source} id={`grad-${source}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={SOURCE_COLORS[source]} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={SOURCE_COLORS[source]} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis
                dataKey="label"
                tick={{ fill: "#888", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "#444" }}
              />
              <YAxis
                tick={{ fill: "#888", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "#444" }}
                tickFormatter={(v) => `$${v}`}
              />
              <Tooltip content={<DailyTooltip />} />
              {allSeries.map((source) =>
                enabledSources[source] !== false ? (
                  <Area
                    key={source}
                    type="linear"
                    dataKey={source}
                    stroke={SOURCE_COLORS[source]}
                    fill={`url(#grad-${source})`}
                    strokeWidth={source === "Total" ? 2.5 : 1.5}
                    strokeDasharray={source === "Total" ? "6 3" : undefined}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ) : null
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Time-of-Day Cumulative Comparison */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
        className="card"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
            <Clock className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold">Spend by Time of Day</h2>
            <p className="text-dark-muted text-xs">Cumulative revenue comparison (15-min intervals)</p>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mb-4">
          {[
            { key: "today", label: `Today (${timeOfDay.todayStr})`, color: TIME_OF_DAY_COLORS.today },
            { key: "yesterday", label: `Yesterday (${timeOfDay.yesterdayStr})`, color: TIME_OF_DAY_COLORS.yesterday },
            { key: "sevenDaysAgo", label: `7 days ago (${timeOfDay.sevenDaysAgoStr})`, color: TIME_OF_DAY_COLORS.sevenDaysAgo },
          ].map((item) => (
            <span key={item.key} className="flex items-center gap-1.5 text-xs text-dark-muted">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
              {item.label}
            </span>
          ))}
        </div>

        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timeOfDay.data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis
                dataKey="label"
                tick={{ fill: "#888", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "#444" }}
                interval={3}
                tickFormatter={(label) => {
                  const hour = label ? String(label).split(":")[0] : "";
                  return hour ? parseInt(hour, 10).toString() : "";
                }}
              />
              <YAxis
                tick={{ fill: "#888", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "#444" }}
                tickFormatter={(v) => `$${v}`}
              />
              <Tooltip content={<TimeTooltip />} />
              {hasNowLabel && (
                <ReferenceLine
                  x={nowLabel}
                  stroke="#fff"
                  strokeOpacity={0.6}
                  strokeDasharray="2 3"
                  label={{ value: "now", position: "top", fill: "#fff", fontSize: 10, opacity: 0.7 }}
                />
              )}
              <Line
                type="linear"
                dataKey="today"
                stroke={TIME_OF_DAY_COLORS.today}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="linear"
                dataKey="yesterday"
                stroke={TIME_OF_DAY_COLORS.yesterday}
                strokeWidth={1.5}
                strokeDasharray="6 3"
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="linear"
                dataKey="sevenDaysAgo"
                stroke={TIME_OF_DAY_COLORS.sevenDaysAgo}
                strokeWidth={1.5}
                strokeDasharray="3 3"
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </motion.div>
      </div>

      {/* Stacked Breakdown: new vs renewal per source */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card mt-6"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold">
              {effectiveGranularity === "month" ? "Monthly" : effectiveGranularity === "week" ? "Weekly" : "Daily"} Revenue: New vs Renewal by Source
            </h2>
            <p className="text-dark-muted text-xs">Stacked. Each source is split into initial purchases and subscription renewals.</p>
          </div>
        </div>

        {breakdownSeries.length === 0 ? (
          <div className="h-[200px] flex items-center justify-center text-dark-muted text-sm">
            No data for this timeframe
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {(() => {
                const newKeys = breakdownSeries.filter((s) => s.endsWith("_NEW"));
                const renKeys = breakdownSeries.filter((s) => s.endsWith("_RENEWAL"));
                const allNewOn = newKeys.every((k) => enabledSources[k] !== false);
                const allRenOn = renKeys.every((k) => enabledSources[k] !== false);
                const allNewOff = newKeys.every((k) => enabledSources[k] === false);
                const allRenOff = renKeys.every((k) => enabledSources[k] === false);
                const activePreset: "total" | "new" | "renewal" | null =
                  allNewOn && allRenOn
                    ? "total"
                    : allNewOn && allRenOff
                    ? "new"
                    : allNewOff && allRenOn
                    ? "renewal"
                    : null;
                const presetBtn = (preset: "total" | "new" | "renewal", label: string) => {
                  const active = activePreset === preset;
                  return (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => applyBreakdownPreset(preset)}
                      className={`px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${
                        active
                          ? "bg-primary-500 text-white"
                          : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
                      }`}
                    >
                      {label}
                    </button>
                  );
                };
                return (
                  <>
                    {presetBtn("total", "All")}
                    {presetBtn("new", "New")}
                    {presetBtn("renewal", "Renew")}
                    <span className="w-px h-5 bg-dark-border mx-1" aria-hidden />
                  </>
                );
              })()}
              {breakdownSeries.map((series) => {
                const active = enabledSources[series] !== false;
                const isRenewal = series.endsWith("_RENEWAL");
                const baseColor = SOURCE_COLORS[series];
                return (
                  <button
                    key={series}
                    onClick={() => toggleSource(series)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                      active ? "border-transparent" : "border-dark-border opacity-40"
                    }`}
                    style={{
                      background: active ? `${baseColor}22` : undefined,
                      color: active ? baseColor : undefined,
                    }}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-sm"
                      style={{
                        background: isRenewal
                          ? `repeating-linear-gradient(45deg, ${baseColor} 0 1.5px, transparent 1.5px 3.5px)`
                          : baseColor,
                        border: isRenewal ? `1px solid ${baseColor}` : undefined,
                      }}
                    />
                    {SOURCE_LABELS[series] || series}
                  </button>
                );
              })}
            </div>

            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <defs>
                    {breakdownSeries
                      .filter((s) => s.endsWith("_RENEWAL"))
                      .map((series) => {
                        const color = SOURCE_COLORS[series];
                        return (
                          <pattern
                            key={series}
                            id={`hatch-${series}`}
                            patternUnits="userSpaceOnUse"
                            width={8}
                            height={8}
                            patternTransform="rotate(45)"
                          >
                            <rect width={8} height={8} fill={color} fillOpacity={0.18} />
                            <line x1={0} y1={0} x2={0} y2={8} stroke={color} strokeWidth={2.5} strokeOpacity={0.95} />
                          </pattern>
                        );
                      })}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: "#888", fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: "#444" }}
                  />
                  <YAxis
                    tick={{ fill: "#888", fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: "#444" }}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip content={<DailyTooltip />} />
                  {weeklyMarkerLabels.map((markerLabel) => (
                    <ReferenceLine
                      key={`week-${markerLabel}`}
                      x={markerLabel}
                      stroke="#fff"
                      strokeOpacity={0.25}
                      strokeDasharray="2 4"
                    />
                  ))}
                  {breakdownSeries.map((series) => {
                    if (enabledSources[series] === false) return null;
                    const isRenewal = series.endsWith("_RENEWAL");
                    return (
                      <Area
                        key={series}
                        type="linear"
                        dataKey={series}
                        stackId="breakdown"
                        stroke={SOURCE_COLORS[series]}
                        fill={isRenewal ? `url(#hatch-${series})` : SOURCE_COLORS[series]}
                        fillOpacity={isRenewal ? 1 : 0.7}
                        strokeWidth={1.2}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    );
                  })}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}

export default function AdminPayments() {
  // Payments state
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [originFilter, setOriginFilter] = useState<PaymentOriginFilter>("all");
  const [timeframe, setTimeframe] = useState<PaymentTimeframeOption>("30d");
  const [granularity, setGranularity] = useState<PaymentChartGranularity>("day");
  const [page, setPage] = useState(1);
  const limit = 200;

  // Referrals state
  const [referralStatusFilter, setReferralStatusFilter] = useState<ReferralStatusFilter>("all");
  const [referralPage, setReferralPage] = useState(1);
  const [paymentsCardExpanded, setPaymentsCardExpanded] = useState(true);
  const [referralsCardExpanded, setReferralsCardExpanded] = useState(false);
  const [showNet, setShowNet] = useState(false);
  const referralLimit = 25;

  const { data: paymentsData, isLoading, isFetching, refetch: refetchPayments } = useAdminPayments({
    page,
    limit,
    status: statusFilter,
    origin: originFilter,
    timeframe,
    refetchInterval: 60_000,
  });

  const { data: referralsData, isLoading: isLoadingReferrals, refetch: refetchReferrals } = useAdminReferrals({
    page: referralPage,
    limit: referralLimit,
    status: referralStatusFilter,
    refetchInterval: 60_000,
  });

  const { data: chartsResponse, isLoading: isLoadingCharts, refetch: refetchCharts } = useAdminPaymentCharts({
    timeframe,
    origin: originFilter,
    granularity,
  });

  const payments = paymentsData?.data?.payments || [];
  const pagination = paymentsData?.data?.pagination;
  const stats = paymentsData?.data?.stats;

  const referralsList = referralsData?.data?.referrals || [];
  const referralsPagination = referralsData?.data?.pagination;
  const referralsStats = referralsData?.data?.stats;

  // Use server-calculated stats
  const totalRevenue = stats?.totalRevenue || 0;
  const totalRefunds = stats?.totalRefunds || 0;
  const totalPayments = stats?.totalPayments || 0;
  const successfulPayments = stats?.successfulPayments || 0;

  // "Today / Yesterday / 7d ago" — pulled from the cumulative time-of-day
  // series so it matches what the chart tooltip shows at the current time.
  // We pick the latest bucket where `today` is populated; yesterday and
  // sevenDaysAgo at that same bucket index are the same-time-of-day values.
  const timeOfDaySeries = chartsResponse?.data?.timeOfDay;
  const dayComparison = useMemo(() => {
    const data = timeOfDaySeries?.data ?? [];
    let lastIdx = -1;
    for (let i = data.length - 1; i >= 0; i--) {
      if (data[i]?.today != null) { lastIdx = i; break; }
    }
    if (lastIdx < 0) return null;
    const row = data[lastIdx];
    return {
      today: Number(row.today) || 0,
      yesterday: Number(row.yesterday) || 0,
      sevenDaysAgo: Number(row.sevenDaysAgo) || 0,
      todayStr: timeOfDaySeries?.todayStr ?? "",
      yesterdayStr: timeOfDaySeries?.yesterdayStr ?? "",
      sevenDaysAgoStr: timeOfDaySeries?.sevenDaysAgoStr ?? "",
    };
  }, [timeOfDaySeries]);

  const pctDelta = (curr: number, base: number) => {
    if (!base) return null;
    return ((curr - base) / base) * 100;
  };

  // Reset to page 1 when filter changes
  const handleStatusChange = (status: StatusFilter) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleOriginChange = (origin: PaymentOriginFilter) => {
    setOriginFilter(origin);
    setPage(1);
  };

  const handleReferralStatusChange = (status: ReferralStatusFilter) => {
    setReferralStatusFilter(status);
    setReferralPage(1);
  };

  const getReferralStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: "bg-yellow-500/20 text-yellow-500",
      used: "bg-blue-500/20 text-blue-500",
      rewarded: "bg-green-500/20 text-green-500",
    };
    return styles[status] || "bg-gray-500/20 text-gray-400";
  };
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      succeeded: "bg-green-500/20 text-green-500",
      failed: "bg-red-500/20 text-red-500",
      pending: "bg-yellow-500/20 text-yellow-500",
      refunded: "bg-gray-500/20 text-gray-400",
    };
    return styles[status] || "bg-gray-500/20 text-gray-400";
  };

  const getPaymentProcessorIcon = (processor: string) => {
    switch (processor) {
      case "APP_STORE":
        return <AppleIcon className="w-4 h-4" />;
      case "PLAY_STORE":
        return <PlayStoreIcon className="w-4 h-4" />;
      case "STRIPE":
      default:
        return <CreditCard className="w-4 h-4" />;
    }
  };

  // Full-page loading only on initial load when we have no data yet
  if (isLoading && !paymentsData) {
    return (
      <div className="flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex items-start justify-between"
      >
        <div>
          <h1 className="text-4xl md:text-5xl font-bold mb-2">Payments</h1>
          <p className="text-dark-muted text-lg">
            View and manage payment transactions
          </p>
        </div>
        <button
          onClick={() => { refetchPayments(); refetchReferrals(); refetchCharts(); }}
          disabled={isFetching}
          className="p-2.5 rounded-xl bg-dark-border text-dark-muted hover:bg-dark-border/70 hover:text-white disabled:opacity-50 transition-colors"
          title="Refresh data"
        >
          <RefreshCw className={`w-5 h-5 ${isFetching ? "animate-spin" : ""}`} />
        </button>
      </motion.div>

      {/* Compact stats — two columns. Left: totals for the selected
          timeframe. Right: same-time-of-day comparison (Today vs Yesterday
          vs 7d ago), otherwise only visible on hover in the time-of-day chart. */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card py-3 px-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 relative"
      >
        {showNet && (
          <span className="absolute top-2 right-3 text-[10px] text-dark-muted italic">stats shown are gross</span>
        )}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-xs text-dark-muted">
              <DollarSign className="w-3 h-3 text-primary-500" /> Revenue
            </span>
            <span className="text-sm font-semibold">${totalRevenue.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-xs text-dark-muted">
              <TrendingUp className="w-3 h-3 text-accent-500" /> Transactions
            </span>
            <span className="text-sm font-semibold">{totalPayments}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-xs text-dark-muted">
              <Users className="w-3 h-3 text-green-500" /> Successful
            </span>
            <span className="text-sm font-semibold">{successfulPayments}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-xs text-dark-muted">
              <DollarSign className="w-3 h-3 text-red-500" /> Refunds
            </span>
            <span className="text-sm font-semibold">${totalRefunds.toFixed(2)}</span>
          </div>
        </div>

        {dayComparison && (
          <div className="flex flex-col gap-1.5 md:border-l md:border-dark-border md:pl-6">
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1.5 text-xs text-dark-muted">
                <span className="w-2 h-2 rounded-full" style={{ background: TIME_OF_DAY_COLORS.today }} />
                Today{dayComparison.todayStr ? ` (${dayComparison.todayStr})` : ""}
              </span>
              <span className="text-sm font-semibold">${dayComparison.today.toFixed(2)}</span>
            </div>
            {(() => {
              const d = pctDelta(dayComparison.today, dayComparison.yesterday);
              return (
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-1.5 text-xs text-dark-muted">
                    <span className="w-2 h-2 rounded-full" style={{ background: TIME_OF_DAY_COLORS.yesterday }} />
                    Yesterday{dayComparison.yesterdayStr ? ` (${dayComparison.yesterdayStr})` : ""}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold">${dayComparison.yesterday.toFixed(2)}</span>
                    {d != null && (
                      <span className={`text-[10px] font-semibold ${d >= 0 ? "text-green-500" : "text-red-500"}`}>
                        {d >= 0 ? "+" : ""}{d.toFixed(0)}%
                      </span>
                    )}
                  </span>
                </div>
              );
            })()}
            {(() => {
              const d = pctDelta(dayComparison.today, dayComparison.sevenDaysAgo);
              return (
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-1.5 text-xs text-dark-muted">
                    <span className="w-2 h-2 rounded-full" style={{ background: TIME_OF_DAY_COLORS.sevenDaysAgo }} />
                    7d ago{dayComparison.sevenDaysAgoStr ? ` (${dayComparison.sevenDaysAgoStr})` : ""}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold">${dayComparison.sevenDaysAgo.toFixed(2)}</span>
                    {d != null && (
                      <span className={`text-[10px] font-semibold ${d >= 0 ? "text-green-500" : "text-red-500"}`}>
                        {d >= 0 ? "+" : ""}{d.toFixed(0)}%
                      </span>
                    )}
                  </span>
                </div>
              );
            })()}
          </div>
        )}
      </motion.div>

      {/* Origin + Timeframe (same row) */}
      <div className="flex flex-wrap items-center justify-between mb-4 gap-y-2">

      <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-dark-muted font-medium">Timeframe:</span>
          {[
            { value: "7d" as const, label: "7D" },
            { value: "30d" as const, label: "30D" },
            { value: "90d" as const, label: "90D" },
            { value: "365d" as const, label: "1Y" },
            { value: "all" as const, label: "All" },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setTimeframe(opt.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                timeframe === opt.value
                  ? "bg-primary-500 text-white"
                  : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
              }`}
            >
              {opt.label}
            </button>
          ))}
          <span className="text-sm text-dark-muted font-medium ml-3">Group:</span>
          {(["day", "week", "month"] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setGranularity(value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize ${
                granularity === value
                  ? "bg-primary-500 text-white"
                  : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
              }`}
            >
              {value}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Gross / Net toggle */}
          <div className="flex items-center rounded-lg overflow-hidden border border-dark-border">
            <button
              type="button"
              onClick={() => setShowNet(false)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                !showNet ? "bg-primary-500 text-white" : "text-dark-muted hover:bg-dark-border/70"
              }`}
            >
              Gross
            </button>
            <button
              type="button"
              onClick={() => setShowNet(true)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                showNet ? "bg-primary-500 text-white" : "text-dark-muted hover:bg-dark-border/70"
              }`}
              title="Stripe: 2.9% + $0.30/txn · App Store &amp; Play Store: 15%"
            >
              Net
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-dark-muted font-medium">Origin:</span>
          {(["all", "STRIPE", "APP_STORE", "PLAY_STORE"] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => handleOriginChange(value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                originFilter === value
                  ? "bg-primary-500 text-white"
                  : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
              }`}
            >
              {value !== "all" && (
                <span className="w-2 h-2 rounded-full" style={{ background: SOURCE_COLORS[value] }} />
              )}
              {value === "all" ? "All" : SOURCE_LABELS[value]}
            </button>
          ))}
          </div>
        </div>
      </div>

      {/* Payment Charts */}
      <PaymentCharts chartsResponse={chartsResponse} isLoadingCharts={isLoadingCharts} granularity={granularity} showNet={showNet} />

      {/* Referrals Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card mb-8"
      >
        <button
          type="button"
          onClick={() => setReferralsCardExpanded((e) => !e)}
          className="flex w-full items-center justify-between mb-4 text-left hover:opacity-90 transition-opacity"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Referrals</h2>
              <p className="text-dark-muted text-sm">
                {referralsStats?.totalReferrals || 0} total referrals
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-yellow-500" />
              <span className="text-dark-muted">{referralsStats?.pendingReferrals || 0} pending</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-blue-500" />
              <span className="text-dark-muted">{referralsStats?.usedReferrals || 0} used</span>
            </div>
            <div className="flex items-center gap-2">
              <Gift className="w-4 h-4 text-green-500" />
              <span className="text-dark-muted">{referralsStats?.rewardedReferrals || 0} rewarded</span>
            </div>
          </div>
          {referralsCardExpanded ? (
            <ChevronUp className="w-5 h-5 text-dark-muted shrink-0" />
          ) : (
            <ChevronDown className="w-5 h-5 text-dark-muted shrink-0" />
          )}
        </button>

        <motion.div
          initial={false}
          animate={{ height: referralsCardExpanded ? "auto" : 0, opacity: referralsCardExpanded ? 1 : 0 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          className="overflow-hidden"
        >
        {/* Referral Status Filter */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {(["all", "pending", "used", "rewarded"] as const).map((status) => (
            <button
              key={status}
              type="button"
              onClick={(e) => { e.stopPropagation(); handleReferralStatusChange(status); }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                referralStatusFilter === status
                  ? "bg-purple-500 text-white"
                  : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {isLoadingReferrals ? (
          <div className="flex items-center justify-center py-8">
            <Loader />
          </div>
        ) : (
          <>
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-dark-card">
                  <tr className="border-b border-dark-border text-left">
                    <th className="pb-3 text-dark-muted font-medium text-sm">Date</th>
                    <th className="pb-3 text-dark-muted font-medium text-sm">Referrer</th>
                    <th className="pb-3 text-dark-muted font-medium text-sm">Referred User</th>
                    <th className="pb-3 text-dark-muted font-medium text-sm">Status</th>
                  </tr>
                </thead>
                <tbody>
                {referralsList.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="pt-8 text-center text-dark-muted">
                      No referrals found
                    </td>
                  </tr>
                ) : (
                  referralsList.map((referral: any, index: number) => (
                    <motion.tr
                      key={referral.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className="border-b border-dark-border/50 hover:bg-dark-border/20 transition-colors"
                    >
                      <td className="py-3 text-sm">
                        {new Date(referral.createdAt).toLocaleString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="py-3">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            {referral.referrer?.id ? (
                              <Link
                                href={`/users?q=${referral.referrer.id}`}
                                className="font-medium text-sm text-primary-400 hover:text-primary-300 hover:underline"
                              >
                                {referral.referrer?.firstName} {referral.referrer?.lastName}
                              </Link>
                            ) : (
                              <span className="font-medium text-sm">
                                {referral.referrer?.firstName} {referral.referrer?.lastName}
                              </span>
                            )}
                            {referral.referrer?.stripeCustomerId && (
                              <a
                                href={stripeCustomerBalanceUrl(referral.referrer.stripeCustomerId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-dark-muted hover:text-primary-400 transition-colors"
                                title="View in Stripe"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                          <span className="text-dark-muted text-xs">
                            {referral.referrer?.email}
                          </span>
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            {referral.referredUser?.id ? (
                              <Link
                                href={`/users?q=${referral.referredUser.id}`}
                                className="font-medium text-sm text-primary-400 hover:text-primary-300 hover:underline"
                              >
                                {referral.referredUser?.firstName} {referral.referredUser?.lastName}
                              </Link>
                            ) : (
                              <span className="font-medium text-sm">
                                {referral.referredUser?.firstName} {referral.referredUser?.lastName}
                              </span>
                            )}
                            {referral.referredUser?.stripeCustomerId && (
                              <a
                                href={stripeCustomerBalanceUrl(referral.referredUser.stripeCustomerId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-dark-muted hover:text-primary-400 transition-colors"
                                title="View in Stripe"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                          <span className="text-dark-muted text-xs">
                            {referral.referredUser?.email}
                          </span>
                        </div>
                      </td>
                      <td className="py-3">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${getReferralStatusBadge(
                            referral.status
                          )}`}
                        >
                          {referral.status}
                        </span>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
            </div>

            {/* Referrals Pagination Controls */}
            {referralsPagination && referralsPagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-dark-border">
                <p className="text-sm text-dark-muted">
                  Showing {(referralsPagination.page - 1) * referralsPagination.limit + 1} to{" "}
                  {Math.min(referralsPagination.page * referralsPagination.limit, referralsPagination.total)} of{" "}
                  {referralsPagination.total} referrals
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setReferralPage((p) => Math.max(1, p - 1))}
                    disabled={referralsPagination.page <= 1}
                    className="p-2 rounded-lg bg-dark-border text-dark-muted hover:bg-dark-border/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-dark-muted px-2">
                    Page {referralsPagination.page} of {referralsPagination.totalPages}
                  </span>
                  <button
                    onClick={() => setReferralPage((p) => Math.min(referralsPagination.totalPages, p + 1))}
                    disabled={referralsPagination.page >= referralsPagination.totalPages}
                    className="p-2 rounded-lg bg-dark-border text-dark-muted hover:bg-dark-border/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        </motion.div>
      </motion.div>

      {/* Payments Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="card overflow-x-auto"
      >
        <button
          type="button"
          onClick={() => setPaymentsCardExpanded((e) => !e)}
          className="flex w-full items-center justify-between mb-4 text-left hover:opacity-90 transition-opacity"
        >
          <h2 className="text-2xl font-bold">Recent Transactions</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(ev) => { ev.stopPropagation(); }}
              className="btn btn-ghost"
            >
              <Download className="w-4 h-4 mr-2 inline-block" />
              Export CSV
            </button>
            {paymentsCardExpanded ? (
              <ChevronUp className="w-5 h-5 text-dark-muted" />
            ) : (
              <ChevronDown className="w-5 h-5 text-dark-muted" />
            )}
          </div>
        </button>

        <motion.div
          initial={false}
          animate={{ height: paymentsCardExpanded ? "auto" : 0, opacity: paymentsCardExpanded ? 1 : 0 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          className="overflow-hidden relative"
        >
        {isFetching && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-dark-card/80 backdrop-blur-[2px]">
            <Loader />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <label className="text-sm font-medium">Filter by status:</label>
          <div className="flex gap-2">
            {(["all", "succeeded", "pending", "failed", "refunded"] as const).map(
              (status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => handleStatusChange(status)}
                  disabled={isFetching}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 ${
                    statusFilter === status
                      ? "bg-primary-500 text-white"
                      : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              )
            )}
          </div>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-dark-border text-left">
              <th className="pb-3 text-dark-muted font-medium text-sm">Date</th>
              <th className="pb-3 text-dark-muted font-medium text-sm">User</th>
              <th className="pb-3 text-dark-muted font-medium text-sm">Type</th>
              <th className="pb-3 text-dark-muted font-medium text-sm">
                Source
              </th>
              <th className="pb-3 text-dark-muted font-medium text-sm">
                Amount
              </th>
              <th className="pb-3 text-dark-muted font-medium text-sm">
                Status
              </th>
              <th className="pb-3 text-dark-muted font-medium text-sm">
                Payment ID
              </th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 ? (
              <tr>
                <td colSpan={7} className="pt-8 text-center text-dark-muted">
                  No payments found
                </td>
              </tr>
            ) : (
              payments.map((payment: any, index: number) => (
                <motion.tr
                  key={payment.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="border-b border-dark-border/50 hover:bg-dark-border/20 transition-colors"
                >
                  <td className="py-4 text-sm">
                    {new Date(payment.createdAt).toLocaleString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="py-4">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        {payment.user?.id ? (
                          <Link
                            href={`/users?q=${payment.user.id}`}
                            className="font-medium text-sm text-primary-400 hover:text-primary-300 hover:underline"
                          >
                            {payment.user?.firstName} {payment.user?.lastName}
                          </Link>
                        ) : (
                          <span className="font-medium text-sm">
                            {payment.user?.firstName} {payment.user?.lastName}
                          </span>
                        )}
                        {payment.stripeCustomerId && (
                          <a
                            href={stripeCustomerUrl(payment.stripeCustomerId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-dark-muted hover:text-primary-400 transition-colors"
                            title="View in Stripe"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                      <span className="text-dark-muted text-xs">
                        {payment.user?.email}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 text-sm">
                    <div className="flex flex-col gap-1">
                      <span className="capitalize">{payment.type}</span>
                      {payment.type === "subscription" && payment.isRenewal === true && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-cyan-500/20 text-cyan-400 w-fit">
                          renewal
                        </span>
                      )}
                      {payment.isRenewal === false && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-500/20 text-green-400 w-fit">
                          new
                        </span>
                      )}
                      {payment.isRenewal == null && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-500/20 text-gray-400 w-fit">
                          unknown
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-4">
                    <span className="text-dark-muted" title={payment.paymentProcessor || "STRIPE"}>
                      {getPaymentProcessorIcon(payment.paymentProcessor || "STRIPE")}
                    </span>
                  </td>
                  <td className="py-4 font-semibold text-sm">
                    {(() => {
                      const gross = parseFloat(payment.amountUsd || payment.amount);
                      const processor = payment.paymentProcessor || "STRIPE";
                      const displayed = showNet ? getNetAmountUsd(gross, processor) : gross;
                      return (
                        <>
                          ${displayed.toFixed(2)}
                          {showNet && (
                            <span className="text-dark-muted text-xs block font-normal">
                              gross ${gross.toFixed(2)}
                            </span>
                          )}
                          {payment.currency && payment.currency !== "usd" && (
                            <span className="text-dark-muted text-xs block font-normal">
                              {parseFloat(payment.amount).toFixed(2)} {payment.currency.toUpperCase()}
                            </span>
                          )}
                          {payment.refundAmount && (
                            <span className="text-red-500 text-xs block">
                              -${parseFloat(payment.refundAmount).toFixed(2)} refunded
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td className="py-4">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(
                          payment.status
                        )}`}
                      >
                        {payment.status}
                      </span>
                      {payment.sandbox && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-500/20 text-orange-400">
                          sandbox
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 text-xs font-mono text-dark-muted">
                    {payment.stripePaymentIntentId || payment.stripeSessionId}
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination Controls */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-dark-border">
            <p className="text-sm text-dark-muted">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
              {pagination.total} payments
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.page <= 1}
                className="p-2 rounded-lg bg-dark-border text-dark-muted hover:bg-dark-border/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                        pagination.page === pageNum
                          ? "bg-primary-500 text-white"
                          : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={pagination.page >= pagination.totalPages}
                className="p-2 rounded-lg bg-dark-border text-dark-muted hover:bg-dark-border/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
        </motion.div>
      </motion.div>
    </div>
  );
}
